import { LegalPage } from '@/components/LegalPage'

/**
 * The privacy notice for the Tech Inventory specifically (issue #13).
 *
 * It is deliberately not the theatre's website policy — that one covers
 * visitors to madstheatre.org, and is linked from here. This system holds a
 * different and much smaller set of personal data: the accounts of the members
 * who run the technical side, and nobody else's.
 *
 * Written to UK GDPR and the Data Protection Act 2018, and public rather than
 * signed-in because Google's consent screen links to it from outside the app.
 */
export function Privacy() {
  return (
    <LegalPage title="Privacy notice" updated="7 August 2026">
      <p>
        This notice explains what the MADS Tech Inventory does with personal data. It covers this
        system only — for the theatre&rsquo;s public website see the{' '}
        <a href="https://madstheatre.org/policies/" target="_blank" rel="noreferrer">
          policies on madstheatre.org
        </a>
        .
      </p>

      <h2>Who we are</h2>
      <p>
        MADS Theatre is a registered charity (number 512929) at Lord Street, Macclesfield, Cheshire,
        SK11 6SY. We are the data controller for the information described here. If you have a
        question about it, speak to the Head of Tech or email{' '}
        <a href="mailto:admin@madstheatre.org">admin@madstheatre.org</a>.
      </p>

      <h2>Who this applies to</h2>
      <p>
        The Tech Inventory is a private system for members who look after the theatre&rsquo;s
        lighting, sound and electrical equipment. Accounts are created by an administrator; there is
        no public sign-up and no account for members of the public. If you do not have an account,
        we hold nothing about you here.
      </p>

      <h2>What we hold, and why</h2>
      <ul>
        <li>
          <strong>Your name and email address</strong> — to identify your account, sign you in, and
          send you password and account emails.
        </li>
        <li>
          <strong>Your phone number, if you give us one</strong> — it is optional, and an account
          works perfectly well without it. It is held for two-step verification of your sign-in and
          for nothing else. Where it is a mobile number we send a verification code to it by text
          message after your password; see below for who delivers that. It is not used to contact
          you about anything else, and it is not used for marketing. Without one you sign in on
          your password alone, unless you have registered a passkey.
        </li>
        <li>
          <strong>Your role and whether your account is active</strong> — to decide what you are
          able to see and change.
        </li>
        <li>
          <strong>A record of changes you make to equipment</strong> — which item, what changed, and
          when. This is how the theatre knows the history of a piece of kit: who last tested it, who
          moved it, who marked it broken. A change made with no connection records{' '}
          <em>when you made it</em> as well as when it reached the system, so the history says what
          actually happened rather than when the device next found a signal.
        </li>
        <li>
          <strong>A record of sign-ins to your account</strong> — when, how you signed in (password,
          passkey, texted code, or Google), and the IP address and browser the request came from. It
          also records attempts that failed: a wrong password, a wrong code, an account turned away.
          This is what lets you tell whether a sign-in was you, which is the whole reason the
          address and browser are there. It never includes your password, the code we texted you, or
          an email address that was typed and did not match an account.
        </li>
        <li>
          <strong>Your password</strong>, stored only as a one-way hash. Nobody at the theatre can
          read it, including administrators.
        </li>
      </ul>
      <p>
        Our lawful basis is legitimate interests: running the theatre&rsquo;s technical operation
        safely, keeping account of charity property, and keeping the accounts that reach it secure.
        We do not use any of it for marketing, we do not profile anyone, and we do not use it to
        contact you about anything other than your account.
      </p>

      <h2>Signing in with Google</h2>
      <p>
        You can sign in with a Google account instead of a password. If you do, Google tells us
        three things: your email address, whether Google has verified it, and the name on the
        account. We ask for nothing else — no access to your mail, contacts, files or calendar.
      </p>
      <p>
        Signing in this way does not create an account. Your Google address has to already match an
        account an administrator has set up here; if it does not, you are turned away and nothing is
        stored. Your use of Google itself is governed by{' '}
        <a href="https://policies.google.com/privacy" target="_blank" rel="noreferrer">
          Google&rsquo;s privacy policy
        </a>
        , not this one.
      </p>

      <h2>Verification codes by text message</h2>
      <p>
        If you have given us a mobile number and have not registered a passkey, we send a six-digit
        code to it each time you sign in. The message is delivered by{' '}
        <a href="https://www.twilio.com/en-us/legal/privacy" target="_blank" rel="noreferrer">
          Twilio
        </a>
        , acting as our processor under contract. To do that they receive your mobile number and
        nothing else &mdash; not your name, not your email address, not anything about the
        equipment. They generate and check the code themselves, so no code is ever stored here.
      </p>
      <p>
        You can ask us to skip this on a browser you use regularly by ticking &ldquo;Trust this
        device&rdquo;, which is ticked by default. That lasts a year, applies to that browser only,
        and never replaces
        your password. You can end it at any time from Your account, and changing your password
        ends it everywhere automatically.
      </p>

      {/*
        * Issue #56. Photographs are of equipment, not of people, so this is
        * not a section about personal data so much as one saying why it is
        * not: the location data a phone puts in a photograph is the one thing
        * here that could identify anybody, and it is discarded rather than
        * kept. AWS is named for the same reason Twilio is above — a processor
        * holding anything of ours is worth stating plainly.
        */}
      <h2>Photographs of equipment</h2>
      <p>
        The system stores a photograph of a piece of equipment or of a kind of lantern, so that a
        name and an asset code are not the only way to tell one from another. These are pictures of
        theatre equipment, not of people.
      </p>
      <p>
        Photographs taken on a phone often carry the place they were taken and the device that took
        them. We discard all of it: every photograph is re-encoded and resized when it is taken,
        which strips that information before anything is stored, and only the reduced copy is kept.
        That happens on your device before the picture is saved anywhere &mdash; so a photograph
        taken where there is no signal, and held until there is, never has the place it was taken
        written on it either.
        The pictures are held in Amazon Web Services storage in the United Kingdom, acting as our
        processor under contract, and are not readable without signing in here. Deleting an item
        permanently deletes its photograph with it.
      </p>

      <h2>Cookies and browser storage</h2>
      <p>
        There is no analytics or advertising here, and nothing tracks you between sites. The system
        stores the following on your device, all of it necessary for it to work:
      </p>
      <ul>
        <li>
          Your sign-in token, kept in your browser&rsquo;s local storage so that you stay signed in
          between visits, and beside it your name, email address and role &mdash; so that the app
          can open and show you who you are signed in as when it cannot reach the server. Signing
          out removes both.
        </li>
        {/*
          * Issue #64, widened by #70. It used to be "the screens you have
          * already looked at"; the device now takes the whole inventory on a
          * schedule, and the photographs with it. A notice that still described
          * the smaller thing would be understating the only thing this system
          * writes to somebody's disk in bulk. Worth saying plainly what is
          * *not* in it: nobody's account.
          */}
        <li>
          A copy of the whole equipment list, the locations, the fixture models and any stocktake in
          progress &mdash; together with the photographs of that equipment &mdash; kept in your
          browser&rsquo;s storage so that the system works in parts of the building with no signal.
          It is fetched again about every quarter of an hour while the app is open and you have a
          connection. It holds equipment records and nothing about any account &mdash; no sign-in
          history, no other member&rsquo;s details, and nothing from the trash. It is discarded
          after seven days, and removed when you sign out.
        </li>
        {/*
          * Issue #64, step 2. The queue is the one thing here that holds work
          * somebody has done rather than a copy of what the server already
          * has, so it is worth saying plainly that it survives signing out —
          * and why, and that it is never sent under anybody else's name.
          */}
        <li>
          Any changes you make while there is no connection &mdash; including the counts you take on
          a stocktake, and any photographs of equipment you take, already reduced and stripped of
          where they were taken &mdash; held on the device until it can send them. They are stored
          against your account, so they survive signing out and back in
          &mdash; a sign-in lasts twelve hours and a walk round the building can outlast it &mdash;
          and they are never sent under anybody else&rsquo;s name. They are removed as soon as they
          reach the inventory, or when you discard them. Anything still unsent after{' '}
          <strong>fourteen days</strong> is refused, and you are shown it rather than it
          disappearing quietly.
        </li>
        <li>
          A short-lived cookie set only while you are signing in with Google, which lasts ten
          minutes and exists to prove the sign-in that comes back is the one you started.
        </li>
        <li>
          A cookie set unless you untick &ldquo;Trust this device&rdquo; when signing in, which lasts a
          year and exists so that browser can skip the texted code. It holds a random value and
          nothing about you.
        </li>
      </ul>

      <h2>Who else sees it</h2>
      <p>
        Other members with an account can see the names attached to equipment records, in the same
        way a paper logbook would show who signed for something. Administrators can additionally see
        account details, including phone numbers, and the sign-in record for any account. You can
        always see your own, under Your account.
      </p>
      <p>
        We do not sell personal data or share it for marketing. It is handled on our behalf only by
        the providers who host the system, deliver its email, and deliver its sign-in text
        messages, under contract and on our instructions. Data is held on servers in the United
        Kingdom; Twilio, who send the text messages, may process a mobile number outside the UK,
        under the safeguards its contract with us requires.
      </p>

      <h2>How long we keep it</h2>
      <p>
        Account details are kept while you are an active member of the technical team, and removed
        when your account is deleted. Equipment history is kept for as long as the theatre owns the
        equipment, because it is the safety and maintenance record for that item; where an entry
        names you, that name may remain part of the record after your account has gone.
      </p>
      <p>
        Sign-in records are kept for <strong>six months</strong> and then deleted automatically.
        They are the one thing here that accumulates simply because you keep using the system, so
        they are the one thing with a time limit on it. The record of who created, changed or
        removed an account is kept alongside the equipment history rather than deleted with the
        sign-ins &mdash; it is an account of decisions, not of movements.
      </p>

      <h2>Your rights</h2>
      <p>
        Under UK data protection law you can ask us for a copy of the personal data we hold about
        you, ask us to correct it if it is wrong, ask us to delete it, ask us to restrict what we do
        with it, or object to our using it. Ask the Head of Tech or email the address above, and we
        will respond within one month.
      </p>
      <p>
        If you are unhappy with how we have handled your data you can complain to the Information
        Commissioner&rsquo;s Office at{' '}
        <a href="https://ico.org.uk" target="_blank" rel="noreferrer">
          ico.org.uk
        </a>{' '}
        or on 0303 123 1113. We would rather you came to us first so we can put it right.
      </p>

      <h2>Changes</h2>
      <p>
        If this notice changes, the date at the top changes with it and the current version is
        always the one on this page.
      </p>
    </LegalPage>
  )
}
